import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import LaunchIcon from "@mui/icons-material/Launch";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageIcon from "@mui/icons-material/Image";
import { BodyText } from "../../../widgets/TemplatePreview";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";

const ShowInteractiveList = ({ data, onOpen, preview = false }) => {
  const [showList, setShowList] = useState(false);
  const renderHeader = () => {
    if (!data.header || !data.header.type) return null;
    const { type, link, text } = data.header;
    switch (type) {
      case "image":
        return (
          <Box
            component="img"
            src={link}
            alt="header"
            sx={{
              width: "100%",
              maxHeight: 250,
              objectFit: "contain",
              borderRadius: 1,
              my: 1,
            }}
          />
        );

      case "video":
        return (
          <Box sx={{ my: 1 }}>
            <Box
              component="video"
              src={link}
              controls
              style={{ width: "100%", borderRadius: 8 }}
            />
          </Box>
        );

      case "document":
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 1.5,
              py: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              my: 1,
            }}
          >
            <PictureAsPdfOutlinedIcon color="action" fontSize="large" />
            <MuiLink
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {link.split("/").pop()}
            </MuiLink>
          </Stack>
        );

      case "text":
        return (
          <Stack sx={{ my: 1 }} direction="row" alignItems="center" spacing={1}>
            <ImageIcon color="disabled" />
            <Typography variant="subtitle2">{text}</Typography>
          </Stack>
        );

      default:
        return (
          <Stack sx={{ my: 1 }} direction="row" alignItems="center" spacing={1}>
            <InsertDriveFileOutlinedIcon color="action" />
            <MuiLink
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {link}
            </MuiLink>
          </Stack>
        );
    }
  };
  /* ---------------- SECTIONS RENDER --------------------------------- */
  const renderSections = () =>
    (data.sections ?? []).map((section) => (
      <Accordion key={section.title} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">{section.title}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <List dense disablePadding>
            {section.rows.map((p) => (
              <ListItem key={p.id} disablePadding sx={{ pl: 2 }}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={500}>{p.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.id}
                      </Typography>
                      <Tooltip title="Copy ID">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(p.id);
                            toast.success("Copied successfully");
                          }}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                  secondary={p.description}
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    ));
  /* ---------------- MAIN RENDER ------------------------------------ */
  return (
    <Container maxWidth="md">
      <Paper variant="outlined" sx={{ p: 2 }}>
        {!preview && (
          <Stack direction="row" spacing={1} flexWrap="wrap" my={1}>
            {(data.triggerText || []).map((item) => (
              <Chip key={item} variant="outlined" label={item} clickable />
            ))}
          </Stack>
        )}
        {renderHeader()}
        <BodyText sx={{ my: 1 }}>{data.body}</BodyText>
        <Button
          variant="contained"
          endIcon={<LaunchIcon />}
          onClick={() => setShowList((prev) => !prev)}
          sx={{ textTransform: "none", my: 1 }}
        >
          {data.btnText}
        </Button>

        {/* Sections (collapsible) */}
        {showList && renderSections()}

        {/* Footer (optional) */}
        {data.footer && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={0.5}
          >
            {data.footer}
          </Typography>
        )}

        {/* “Add trigger” action */}
        {!preview && (
          <Stack direction="row" justifyContent="end" mt={2}>
            <Button
              onClick={() => onOpen?.()}
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
            >
              Add trigger
            </Button>
          </Stack>
        )}
      </Paper>
    </Container>
  );
};

export default ShowInteractiveList;
